// 📁 src/components/Payment/PaymentPage.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import RomanBanner from '../../Common/RomanBanner';
import OrderSummaryPanel from './OrderSummaryPanel';
import PaymentMethodSelector from './PaymentMethodSelector';
import CreditCardForm from './CreditCardForm';
import BkashForm from './BkashForm';
import '../../styles/payment.css';

const PaymentPage = () => {
  const location = useLocation();
  const state = location.state || JSON.parse(sessionStorage.getItem('mockPaymentState')) || {};

  const {
    tenantId: passedTenantId = '',
    companyName = '',
    method = 'Card',
    onPaymentSuccess = () => alert('Payment successful'),
    onPaymentFailure = (err) => alert('Payment failed: ' + err)
  } = state || {};

  const stableCompany = useMemo(() => companyName, []);
  const [selectedMethod, setSelectedMethod] = useState(method);
  const [promoDetails, setPromoDetails] = useState(null);
  const [config, setConfig] = useState({});
  const [selectedModules, setSelectedModules] = useState([]);
  const [currency, setCurrency] = useState('BDT');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [tenantId, setTenantId] = useState(passedTenantId);
  const [resolvedCompanyName, setResolvedCompanyName] = useState('');
  const [accountCountry, setAccountCountry] = useState(''); // ✅ track country

  useEffect(() => {
    const email =
      sessionStorage.getItem('isLoggedIn') === 'true' ? localStorage.getItem('email') : null;
    if (!email) return;

    const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
    const account = registry[email] || {};
    const resolvedTenantId = account.tenantId || email;
    setTenantId(resolvedTenantId);

    const fallbackCompany = companyName?.trim() || account.companyName?.trim() || '';
    setResolvedCompanyName(fallbackCompany);

    // ✅ capture country from accountRegistry
    setAccountCountry(account.country || '');

    const configData = JSON.parse(localStorage.getItem('adminPricingConfig')) || {};
    let promo = JSON.parse(localStorage.getItem(`activePromo_${resolvedTenantId}`)) || null;

    if (!promo || !promo.discountRate) {
      console.warn(`⚠️ Scoped promo not found for ${resolvedTenantId}. Trying global fallback.`);
      promo = JSON.parse(localStorage.getItem('activePromo') || null);
    }

    const cartItems = JSON.parse(localStorage.getItem(`cartItems_${resolvedTenantId}`) || '[]');
    const currencyKey = `currency_${resolvedTenantId}`;
    const scopedCurrency = localStorage.getItem(currencyKey) || configData.currency || 'BDT';
    const scopedBilling =
      localStorage.getItem('billingCycle') || configData.billingCycle || 'monthly';

    setCurrency(scopedCurrency);
    setBillingCycle(scopedBilling);
    setConfig(configData);

    const hydratedModules = cartItems.map((item) => ({
      name: item.name,
      price: item.price * item.quantity
    }));

    setSelectedModules(hydratedModules);

    const today = new Date();
    let promoApplied = false;
    if (promo) {
      const from = new Date(promo.validFrom);
      const to = new Date(promo.validTo);
      if (today >= from && today <= to && promo.discountRate > 0) {
        promoApplied = true;
        setPromoDetails(promo);
      } else {
        console.warn(`⚠️ Promo expired or invalid for ${resolvedTenantId}`);
      }
    }

    const logEntry = {
      timestamp: today.toISOString(),
      tenantId: resolvedTenantId,
      companyName: fallbackCompany,
      selectedModules: hydratedModules,
      billingCycle: scopedBilling,
      currency: scopedCurrency,
      promoApplied,
      promoDetails: promoApplied ? promo : null
    };

    try {
      const auditKey = `paymentAuditLog_${resolvedTenantId}`;
      const existingLogs = JSON.parse(localStorage.getItem(auditKey)) || [];
      const cappedLogs = [...existingLogs, logEntry].slice(-100);
      localStorage.setItem(auditKey, JSON.stringify(cappedLogs));
    } catch (error) {
      console.warn('Audit log skipped due to quota limit:', error);
    }
  }, []);

  return (
    <div className="payment-page">
      <RomanBanner text={`Talentraxion Payment Portal — ${resolvedCompanyName}`} />
      <h2 className="text-xl font-semibold text-gray-800 mb-4">ATS Subscription Payment</h2>

      <OrderSummaryPanel
        selectedModules={selectedModules}
        billingCycle={billingCycle}
        currency={currency}
        discountRate={config.annualDiscountRate || 0}
        promoName={promoDetails?.occasion || ''}
        promoDiscountRate={promoDetails?.discountRate || 0}
        promoDurationMonths={promoDetails?.benefitDurationMonths || 0}
        tenantId={tenantId}
      />

      <PaymentMethodSelector selectedMethod={selectedMethod} onSelect={setSelectedMethod} />

      {selectedMethod === 'card' && (
        <CreditCardForm
          tenantId={tenantId}
          companyName={resolvedCompanyName}
          currency={currency}
          billingCycle={billingCycle}
          selectedModules={selectedModules}
          annualDiscountRate={config.annualDiscountRate || 0}
          onSuccess={onPaymentSuccess}
          onFailure={onPaymentFailure}
        />
      )}

      {selectedMethod === 'bkash' && (
        <BkashForm
          tenantId={tenantId}
          companyName={resolvedCompanyName}
          currency={currency}
          country={accountCountry} // ✅ pass country down
          billingCycle={billingCycle}
          selectedModules={selectedModules}
          annualDiscountRate={config.annualDiscountRate || 0}
          onSuccess={onPaymentSuccess}
          onFailure={onPaymentFailure}
        />
      )}
    </div>
  );
};

export default PaymentPage;
